/**
 * TASK-TGE-007 & TASK-TGE-008: Grid Visibility Controller
 *
 * Controls grid visibility based on battle phase with smooth fade transitions.
 * Deployment phase shows full grid, combat phase shows only border glow.
 */

import { BattlePhase } from '../../types/terrain';

/**
 * Determine if the full grid should be visible based on battle phase
 *
 * @param phase - Current battle phase
 * @returns True if grid should be fully visible, false if only borders should show
 */
export function shouldShowGrid(phase: BattlePhase): boolean {
  switch (phase) {
    case BattlePhase.DEPLOYMENT:
      return true; // Full grid visible during deployment

    case BattlePhase.COUNTDOWN:
    case BattlePhase.COMBAT:
    case BattlePhase.FINISHED:
      return false; // Only borders visible during combat
  }
}

/**
 * Calculate the opacity of the grid during phase transitions
 * Provides smooth fade animation between phases
 *
 * @param phase - Current battle phase
 * @param transitionProgress - Progress through the current transition (0-1)
 * @returns Alpha value for grid opacity (0-1)
 */
export function calculateGridFadeAlpha(
  phase: BattlePhase,
  transitionProgress: number
): number {
  switch (phase) {
    case BattlePhase.DEPLOYMENT:
      // Grid is fully visible
      return 1.0;

    case BattlePhase.COUNTDOWN:
      // Fading out during countdown
      // transitionProgress 0 = just entered countdown (alpha = 1)
      // transitionProgress 1 = countdown complete (alpha = 0)
      return 1.0 - transitionProgress;

    case BattlePhase.COMBAT:
    case BattlePhase.FINISHED:
      // Grid is fully hidden (borders still visible)
      return 0.0;

    default:
      return 0.0;
  }
}

/**
 * State tracker for grid fade animation
 */
export class GridFadeAnimator {
  private startTime: number = 0;
  private duration: number = 500; // Default 500ms fade
  private isAnimating: boolean = false;
  private targetAlpha: number = 1.0;
  private startAlpha: number = 1.0;
  private currentAlpha: number = 1.0;

  /**
   * Start a fade animation to a target alpha
   *
   * @param targetAlpha - Target alpha value (0-1)
   * @param duration - Duration of the fade in milliseconds
   */
  startFade(targetAlpha: number, duration: number): void {
    this.startTime = performance.now();
    this.duration = duration;
    this.startAlpha = this.currentAlpha;
    this.targetAlpha = targetAlpha;
    this.isAnimating = true;
  }

  /**
   * Update the animation and get the current alpha value
   *
   * @returns Current alpha value (0-1)
   */
  update(): number {
    if (!this.isAnimating) {
      return this.currentAlpha;
    }

    const elapsed = performance.now() - this.startTime;
    const progress = Math.min(1.0, elapsed / this.duration);

    // Ease-out interpolation
    const easedProgress = progress * (2 - progress);
    this.currentAlpha =
      this.startAlpha + (this.targetAlpha - this.startAlpha) * easedProgress;

    // Stop animating when complete
    if (progress >= 1.0) {
      this.currentAlpha = this.targetAlpha;
      this.isAnimating = false;
    }

    return this.currentAlpha;
  }

  /**
   * Check if currently animating
   */
  get animating(): boolean {
    return this.isAnimating;
  }

  /**
   * Get current alpha without updating
   */
  get alpha(): number {
    return this.currentAlpha;
  }

  /**
   * Immediately set alpha without animation
   */
  setAlpha(alpha: number): void {
    this.currentAlpha = alpha;
    this.targetAlpha = alpha;
    this.startAlpha = alpha;
    this.isAnimating = false;
  }
}
